const SNAP_MS = 300;
const FLICK_VELOCITY = 1.2; // px/ms — a drag faster than this advances an extra card
const DRAG_THRESHOLD = 5; // px of movement before a drag suppresses the card's click

// Trackpad momentum arrives as wheel events with decaying deltas; once the
// latest delta is this small, the momentum has settled and we can snap.
const WHEEL_SETTLE_DELTA = 1.5;
const WHEEL_SETTLE_MS = 60; // snap shortly after the momentum tail ends
const WHEEL_IDLE_MS = 250; // fallback when deltas stop while still sizeable

// Linear "tent" falloff: full boost at the focus, tapering to zero one card away.
// This is a partition of unity (contributions always sum to 1), so the total
// track width is constant regardless of position — the ring loops without drift.
const tent = (d) => Math.max(0, 1 - Math.abs(d));

// Which copy of card `c` on the ring sits nearest the continuous focus `f`.
const ringIndex = (c, f, count) => c + count * Math.round((f - c) / count);

// One card is kept just off the left edge as a buffer for backward dragging;
// the rest trail to the right of the focus, filling the viewport.
const LEFT_BUFFER = 1;

// Which copy of card `c` to render: the one falling in the window that starts a
// buffer-card left of the focus `f` and runs rightwards over the whole ring.
const renderIndex = (c, f, count) => c + count * Math.ceil((f - LEFT_BUFFER - c) / count);

const easeOutCubic = (t) => 1 - (1 - t) ** 3;

// Wrap `x` into the range [0, n) (JS `%` keeps the sign of the dividend).
const wrap = (x, n) => ((x % n) + n) % n;

// The geometry custom properties must be registered as <length> (see the
// stylesheet) for getComputedStyle to return resolved pixels. Without @property
// support we leave the static, natively-scrollable fallback in place.
const supportsRegisteredProps = typeof CSS !== 'undefined' && !!CSS.registerProperty;

class TopicCarouselElement extends HTMLElement {
  connectedCallback() {
    if (!supportsRegisteredProps) return;

    this._viewport = this.querySelector('.topic-carousel__viewport');
    this._track = this.querySelector('.topic-carousel__track');
    this._dots = Array.from(this.querySelectorAll('.topic-carousel__dot'));
    const cards = Array.from(this.querySelectorAll('.topic-carousel-card'));

    if (!this._track || cards.length === 0) return;

    this._cards = cards.map((card) => ({
      card,
      media: card.querySelector('.topic-carousel-card__media'),
    }));
    this._count = cards.length;
    this._offset = 0; // continuous focus position along the ring, in px
    this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._enabled = false;
    this._readGeometry();

    this._controller = new AbortController();
    const { signal } = this._controller;

    this._viewport.addEventListener('pointerdown', this._onPointerDown, { signal });
    // Cancel native image/link dragging so it can't fight the pointer drag —
    // consumers don't need to mark their images undraggable.
    this._viewport.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
    window.addEventListener('pointermove', this._onPointerMove, { signal });
    window.addEventListener('pointerup', this._onPointerUp, { signal });
    this._viewport.addEventListener('wheel', this._onWheel, { passive: false, signal });
    this._track.addEventListener('click', this._onClickCapture, { capture: true, signal });
    this._track.addEventListener('focusin', this._onFocusIn, { signal });

    this.querySelector('[data-ref="prev"]')?.addEventListener('click', () => this._step(-1), {
      signal,
    });
    this.querySelector('[data-ref="next"]')?.addEventListener('click', () => this._step(1), {
      signal,
    });

    // Enhance only when the cards don't already fit; re-evaluate on resize.
    this._resizeObserver = new ResizeObserver(() => this._update());
    this._resizeObserver.observe(this._viewport);
  }

  disconnectedCallback() {
    this._controller?.abort();
    this._resizeObserver?.disconnect();
    this._cancelTween?.();
    clearTimeout(this._wheelTimer);
  }

  // --- Geometry: read from the CSS custom properties (responsive) --------------

  _readGeometry() {
    const cs = getComputedStyle(this);
    const baseW = parseFloat(cs.getPropertyValue('--tc-base-w'));
    const baseH = parseFloat(cs.getPropertyValue('--tc-base-h'));
    const focusW = parseFloat(cs.getPropertyValue('--tc-focus-w'));
    const gap = parseFloat(cs.getPropertyValue('--tc-gap'));

    if (!baseW) return false; // not laid out yet

    const prevSlot = this._slot;
    this._baseW = baseW;
    this._baseH = baseH;
    this._focusW = focusW;
    this._gap = gap;
    this._slot = baseW + gap; // distance between successive card left edges at base scale
    this._boost = focusW / baseW - 1; // extra scale applied to the focused card
    this._bulge = focusW - baseW; // extra width the focused card adds when fully scaled
    this._loop = this._count * this._slot; // one full revolution of the ring, in px

    // Keep the focus on the same card when the geometry changes (e.g. a
    // breakpoint cross) by rescaling the offset to the new slot size.
    if (prevSlot) this._offset = (this._offset / prevSlot) * this._slot;
    return true;
  }

  _scaleAt(d) {
    return 1 + this._boost * tent(d);
  }

  // Continuous left-edge position for a card `d` cards away from the focus.
  // Pieced together so neighbouring cards stay exactly the gap apart at every
  // `d` (and so there is no jump as `d` crosses an integer): left of/at the
  // focus uses the base slot; right of the bulge is shifted by the focus card's
  // extra width; the focus-to-next gap interpolates between the two.
  _positionAt(d) {
    if (d <= 0) return d * this._slot;
    if (d >= 1) return d * this._slot + this._bulge;
    return d * (this._slot + this._bulge);
  }

  // --- Enable / disable based on available width -------------------------------

  _update() {
    const available = this._viewport.clientWidth;
    if (available === 0) return; // not laid out yet
    if (!this._readGeometry()) return;

    const contentWidth = this._count * this._baseW + (this._count - 1) * this._gap;
    const shouldEnable = contentWidth > available;

    if (shouldEnable !== this._enabled) {
      this._setEnabled(shouldEnable);
    } else if (this._enabled) {
      // Already enabled but the geometry may have changed — re-render at the new size.
      this._render();
    }
  }

  _setEnabled(enabled) {
    this._enabled = enabled;
    this.classList.toggle('topic-carousel--enhanced', enabled);

    if (enabled) {
      this._render();
      return;
    }

    // Hand layout back to the static fallback: drop everything we set inline.
    this._cancelTween?.();
    this._dragging = false;
    this._offset = 0;
    this._cards.forEach(({ card, media }) => {
      card.style.transform = '';
      media.style.width = '';
      media.style.height = '';
    });
  }

  // --- Rendering: map the current offset to each card's position and scale -----

  _render() {
    const f = this._offset / this._slot;

    this._cards.forEach(({ card, media }, c) => {
      const d = renderIndex(c, f, this._count) - f;
      const s = this._scaleAt(d);

      card.style.transform = `translate3d(${this._positionAt(d)}px, 0, 0)`;
      media.style.width = `${this._baseW * s}px`;
      media.style.height = `${this._baseH * s}px`;
    });

    this._updateDots();
  }

  // Mirror the resting card under the focus onto the mobile dot indicators.
  _updateDots() {
    if (this._dots.length === 0) return;

    const index = wrap(this._index, this._count);
    if (index === this._activeDot) return;
    this._activeDot = index;

    this._dots.forEach((dot, i) =>
      dot.classList.toggle('topic-carousel__dot--active', i === index)
    );
  }

  // --- Movement ----------------------------------------------------------------

  _normalize() {
    this._offset = wrap(this._offset, this._loop);
  }

  _animateTo(target) {
    this._cancelTween?.();

    if (this._reduceMotion) {
      this._offset = target;
      this._normalize();
      this._render();
      return;
    }

    const start = this._offset;
    const dist = target - start;
    const t0 = performance.now();
    let raf;

    const step = (now) => {
      const t = Math.min(1, (now - t0) / SNAP_MS);
      this._offset = start + dist * easeOutCubic(t);
      this._render();
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        this._normalize();
        this._render();
      }
    };

    raf = requestAnimationFrame(step);
    this._cancelTween = () => cancelAnimationFrame(raf);
  }

  // Nearest resting card index for the current offset.
  get _index() {
    return Math.round(this._offset / this._slot);
  }

  _glideToCard(index) {
    this._animateTo(index * this._slot);
  }

  _snap(velocity = 0) {
    const bump = Math.abs(velocity) > FLICK_VELOCITY ? Math.sign(velocity) : 0;
    this._glideToCard(this._index + bump);
  }

  _step(direction) {
    if (this._enabled) this._glideToCard(this._index + direction);
  }

  // --- Pointer drag (covers mouse drag and touch swipe) ------------------------

  _onPointerDown = (e) => {
    if (!this._enabled || e.button > 0) return;
    this._cancelTween?.();
    this._dragging = true;
    this._dragMoved = false;
    this._startX = e.clientX;
    this._startOffset = this._offset;
    this._lastX = e.clientX;
    this._lastT = performance.now();
    this._velocity = 0;
    this._pointerId = e.pointerId;
  };

  _onPointerMove = (e) => {
    if (!this._dragging) return;
    const dx = e.clientX - this._startX;
    // Capture only once a real drag starts. Capturing on pointerdown would
    // retarget the click to the viewport, so a plain tap never reaches the link.
    if (!this._dragMoved && Math.abs(dx) > DRAG_THRESHOLD) {
      this._dragMoved = true;
      this._viewport.setPointerCapture?.(this._pointerId);
    }

    this._offset = this._startOffset - dx;

    const now = performance.now();
    const dt = now - this._lastT;
    if (dt > 0) this._velocity = -(e.clientX - this._lastX) / dt;
    this._lastX = e.clientX;
    this._lastT = now;

    this._render();
  };

  _onPointerUp = () => {
    if (!this._dragging) return;
    this._dragging = false;
    this._snap(this._velocity);
  };

  // Stop a drag from triggering navigation on the underlying link.
  _onClickCapture = (e) => {
    if (this._dragMoved) {
      e.preventDefault();
      e.stopPropagation();
      this._dragMoved = false;
    }
  };

  // --- Wheel / trackpad (horizontal intent only) -------------------------------

  _onWheel = (e) => {
    if (!this._enabled) return;
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // leave vertical scrolling to the page
    e.preventDefault();
    this._cancelTween?.();
    this._offset += e.deltaX;
    this._render();

    // Snap once the momentum tail decays to a near-zero delta; otherwise wait a
    // longer idle in case scrolling stopped abruptly (fingers-down — undetectable).
    const settled = Math.abs(e.deltaX) < WHEEL_SETTLE_DELTA;
    clearTimeout(this._wheelTimer);
    this._wheelTimer = setTimeout(() => this._snap(0), settled ? WHEEL_SETTLE_MS : WHEEL_IDLE_MS);
  };

  // --- Keyboard: bring a focused card into the focus position ------------------

  _onFocusIn = (e) => {
    // Only re-centre on keyboard focus. A pointer press also focuses the link,
    // but `_dragging` is already set by then — skip it so the focus tween can't
    // fight an in-progress drag (or yank the card left on a plain click).
    if (!this._enabled || this._dragging) return;
    const card = e.target.closest('.topic-carousel-card');
    if (!card) return;
    const c = Number(card.dataset.index);
    this._glideToCard(ringIndex(c, this._offset / this._slot, this._count));
  };
}

customElements.define('chh-topic-carousel', TopicCarouselElement);

export default TopicCarouselElement;
